/* global axios */
import ApiClient from './ApiClient';

class BillingAPI extends ApiClient {
  constructor() {
    super('billing', { accountScoped: true });
  }

  // Get all available plans
  getPlans() {
    return axios.get(`${this.url}/plans`);
  }

  // Get current subscription details
  getCurrentSubscription() {
    return axios.get(`${this.url}/current`);
  }

  // Get usage metrics
  getUsage() {
    return axios.get(`${this.url}/usage`);
  }

  // Subscribe to a plan
  subscribe(planId, billingCycle = 'monthly', paymentMethod = 'BOLETO') {
    return axios.post(`${this.url}/subscribe`, {
      plan_id: planId,
      billing_cycle: billingCycle,
      payment_method: paymentMethod,
    });
  }

  // Upgrade to a higher plan
  upgrade(planId) {
    return axios.post(`${this.url}/upgrade`, {
      plan_id: planId,
    });
  }

  // Downgrade to a lower plan
  downgrade(planId) {
    return axios.post(`${this.url}/downgrade`, {
      plan_id: planId,
    });
  }

  // Cancel subscription
  cancel(reason = null, immediate = false) {
    return axios.post(`${this.url}/cancel`, {
      reason,
      immediate,
    });
  }

  // Get invoice history
  getInvoices() {
    return axios.get(`${this.url}/invoices`);
  }

  // Get current usage and limits
  getUsage() {
    return axios.get(`${this.url}/usage`);
  }

  // Get limits status for all resources
  getLimits() {
    return axios.get(`${this.url}/limits`);
  }

  // Get available add-ons catalog
  getAddons() {
    return axios.get(`${this.url}/addons`);
  }

  // Get current account's add-ons
  getMyAddons() {
    return axios.get(`${this.url}/my_addons`);
  }

  // Purchase an add-on. The endpoint creates the add-on + a pro-rata invoice
  // with PIX (self-service). Returns { addon, invoice, needs_billing_info } or
  // { addon, needs_billing_info: true } when the ASAAS customer is not set up.
  getModules() {
    return axios.get(`${this.url}/modules`);
  }

  purchaseAddon(addonId, quantity = 1, notes = '') {
    return axios.post(`${this.url}/purchase_addon`, {
      addon_id: addonId,
      quantity,
      notes,
    });
  }

  // Alias for the self-service buy & pay flow (same endpoint as purchaseAddon).
  payAddon(addonId, quantity = 1) {
    return this.purchaseAddon(addonId, quantity);
  }

  // Buy SEVERAL add-ons in one invoice (upgrade cart). items = [{ addon_id, quantity }].
  // Returns { addons, invoice, needs_billing_info } (one bundled PIX).
  purchaseCart(items) {
    return axios.post(`${this.url}/purchase_cart`, { items });
  }

  // Cancel an add-on
  cancelAddon(addonId, immediate = false) {
    return axios.delete(`${this.url}/cancel_addon/${addonId}`, {
      data: { immediate },
    });
  }

  // Get current credit balance
  getCredits() {
    return axios.get(`${this.url}/credits`);
  }

  // Get available credit packages
  getCreditPackages() {
    return axios.get(`${this.url}/credit_packages`);
  }

  // Purchase credits
  purchaseCredits(packageId, quantity = 1, paymentMethod = 'PIX') {
    return axios.post(`${this.url}/purchase_credits`, {
      package_id: packageId,
      quantity,
      payment_method: paymentMethod,
    });
  }

  // Create a Stripe Checkout session (returns checkout_url for redirect)
  // invoiceId: fatura que a tela está mostrando — o cartão cobra exatamente ela.
  createCheckout(planId, billingCycle = 'monthly', invoiceId = null) {
    return axios.post(`${this.url}/checkout`, {
      plan_id: planId,
      billing_cycle: billingCycle,
      ...(invoiceId ? { invoice_id: invoiceId } : {}),
    });
  }

  // Assinatura recorrente no cartão (Stripe Checkout). Caminho de quem não tem
  // PIX/boleto — cliente de fora do Brasil.
  subscribeCard() {
    return axios.post(`${this.url}/subscribe_card`);
  }

  // Create a Stripe Customer Portal session (returns portal_url for redirect)
  createPortal() {
    return axios.post(`${this.url}/portal`);
  }

  // Get invoice preview (next billing calculation)
  getInvoicePreview() {
    return axios.get(`${this.url}/invoice_preview`);
  }

  // Get current pending invoice
  getCurrentInvoice() {
    return axios.get(`${this.url}/current_invoice`);
  }

  // Get invoice history with pagination
  getInvoiceHistory(page = 1, perPage = 10) {
    return axios.get(`${this.url}/invoice_history`, {
      params: { page, per_page: perPage },
    });
  }

  // Generate a new invoice for the current period
  generateInvoice() {
    return axios.post(`${this.url}/generate_invoice`);
  }

  // Generate PIX payment for an existing invoice
  generatePixForInvoice(invoiceId) {
    return axios.post(`${this.url}/invoices/${invoiceId}/generate_pix`);
  }

  // Get paywall data (works for suspended accounts)
  getPaywallInfo() {
    return axios.get(`${this.url}/paywall`);
  }

  // Self-signup: salva CPF/CNPJ + cria o cliente ASAAS (1ª cobrança)
  saveFirstPaymentInfo({ cpfCnpj, name, phone }) {
    return axios.post(`${this.url}/first_payment/info`, {
      cpf_cnpj: cpfCnpj,
      name,
      phone,
    });
  }

  // Self-signup: gera a 1ª fatura (mês cheio, sem pró-rata) + PIX
  generateFirstPaymentInvoice() {
    return axios.post(`${this.url}/first_payment/invoice`);
  }

  // Generate Boleto payment for an existing invoice
  generateBoletoForInvoice(invoiceId) {
    return axios.post(`${this.url}/invoices/${invoiceId}/generate_boleto`);
  }

  // Poll payment status for an invoice
  getPaymentStatus(invoiceId) {
    return axios.get(`${this.url}/invoices/${invoiceId}/status`);
  }

  // Request NFS-e (Nota Fiscal) emission for a paid invoice
  requestNfse(invoiceId) {
    return axios.post(`${this.url}/invoices/${invoiceId}/request_nfse`);
  }
}

export default new BillingAPI();
